import React from 'react';
import BqnContext from "common/BqnContext";
import styled from "styled-components";
import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HelpModal } from "./HelpModal";
import ActionsContext from "common/ActionsContext";
import { parseStrTable } from "common/api";
import { Header } from "common/Header";
import { ClusterSummary } from "./ClusterSummary";
import { BqnButton } from "../Dashboard/BqnButton";
import { getIndexes } from "common/utils";
import ClusterThroughput from "./clusterThroughput/ClusterThroughput";
import { useTime } from "common/hooks";
import {
  getFieldsAndMap,
  parseHostnames,
  useMockStatisticsShort,
} from "./utils";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";

const Div = styled.div`
  .block-header {
    margin: 0 auto 15px auto;
    max-width: 1080px;
  }

  a {
    cursor: pointer;
  }

  .header {
    color: #555;
    position: relative;
  }

  .header .header-dropdown {
    position: absolute;
    top: 0px;
    right: 12px;
    list-style: none;
  }

  .header .header-dropdown i {
    color: #999;
    -moz-transition: all 0.5s;
    -o-transition: all 0.5s;
    -webkit-transition: all 0.5s;
    transition: all 0.5s;
  }

  .header .header-dropdown i:hover {
    color: #000;
  }

  .flex-container {
    display: flex;
    flex-direction: row;
    gap: 15px;

    > div {
      flex-grow: 1;
    }
  }
`;

const GridContainer = styled("div")(({ columns }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gridColumnGap: 15,
  gridRowGap: 15,
}));

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help")}
      >
        <i className="material-icons">
          <small>help_outline</small>
        </i>
      </a>
      <a
        onClick={() => actions.send("do-load")}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">
          <small>refresh</small>
        </i>
      </a>
    </ul>
  );
};

const nColumnsMap = {
  1: 1,
  2: 2,
  3: 3,
  4: 2,
  5: 3,
  6: 3,
  7: 4,
  8: 3,
  9: 3,
};

const timeWindow = 10;

export function ClusterDashboard({ test }) {
  const [alarms, setAlarms] = useState({});
  const [hostnames, setHostnames] = useState({});
  const [totalSubscriberGroups, setTotalSubscriberGroups] = useState({});
  const [resetSignal, setResetSignal] = useState(false);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState({ map: {}, fields: [] });
  const { bqns, setBqnId, loadClusterStatus, loginBqnId, bqnId } =
    useContext(BqnContext);
  const navigate = useNavigate();
  const actions = useContext(ActionsContext);
  const nColumns = bqns.length < 10 ? nColumnsMap[bqns.length] : 5;
  const gridLimit = bqns.length - (bqns.length % nColumns);
  const [seconds] = useTime(true, 1000);
  const lastValue = useRef({});
  const maxValue = useRef({ downlink: 0, uplink: 0 });
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const getMock = useMockStatisticsShort();
  const headerIndex = useRef();
  const isMounted = useRef(true);
  const lastSecond = useRef(null);

  const { map, fields } = subs || {};

  useEffect(() => {
    let timeoutId;

    setBqnId("all");

    const doLoad = () => {
      if (!isMounted.current) return;
      Promise.all([loadClusterStatus(), loadData()]).then(() => {
        if (isMounted.current) {
          timeoutId = setTimeout(doLoad, 4000);
        }
      });
    };

    doLoad();
    const unsubscribe = actions.recv("do-load", () => {
      maxValue.current = { downlink: 0, uplink: 0 };
      lastValue.current = {};
      setResetSignal((value) => !value);
      doLoad();
    });

    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (bqnId !== "all") {
      navigate("/viewDashboard");
    }
  }, [bqnId]);

  useEffect(() => {
    if(document.visibilityState === "visible"){
      doLoadStatisticsShort();
    }
  }, [seconds]);

  const loadData = () =>
    ifCl
      .run(
        "show system hostname detail\nshow api subscriber-group status aggregate\nshow system alarms\n",
        true
      )
      .then((response) => {
        const [hostnameResponse, subscriberGroupsResponse, alarmsResponse] = response.split(",");
        setHostnames(parseHostnames(hostnameResponse));

        const result = {};
        if(alarmsResponse){
          parseStrTable(alarmsResponse, function (attrs, i) {
            const [bqn, alarm, level] = attrs;
            const entry = { alarm, level };
            if (result[bqn]) {
              result[bqn].push(entry);
            } else {
              result[bqn] = [entry];
            }
          });
        }
        setAlarms(result);
        setTotalSubscriberGroups(
          parseNSubscriberGroups(subscriberGroupsResponse)
        );
      });

  function onBqnSelected(bqn) {
    if (bqn.function === "offline") {
      return;
    }

    setBqnId(bqn.bqnId);
  }

  const doLoadStatisticsShort = () => {
    ifCl.run("show pkteng statistics short").then((response) => {
      if (test) {
        response = getMock();
      }

      const result = parseStatisticsShort(response);
      const { throughput } = result || {};

      setData(result);

      if (!result) {
        return;
      }

      const oldFieldsMap = { ...subs.map };
      const newFieldKeys = throughput.series
        ? Object.keys(throughput.series)
        : [];
      const newFieldNamesSet = new Set([
        ...newFieldKeys,
        ...Object.keys(oldFieldsMap),
      ]);

      setSubs(
        getFieldsAndMap(
          newFieldNamesSet,
          oldFieldsMap,
          colorsSet,
          throughput.series,
          throughput.time,
          timeWindow
        )
      );

      const currentSecond = new Date().getSeconds();
      if(lastSecond.current !== null && currentSecond - lastSecond.current === 0){
        return;
      }

      lastSecond.current = currentSecond;

      setSummary({
        totalDownlink: result.totalDownlink,
        totalUplink: result.totalUplink,
        totalSubscribers: result.totalSubscribers,
        totalFlows: result.totalFlows,
        optPercentage: result.optPercentage,
      })
    });
  };

  function parseNSubscriberGroups(res) {
    if (!res){
      return 0;
    }
    const [header, ...lines] = res.trim().split("\n");
    const headerIndex = getIndexes(header);
    const attrs = lines[0].trim().split(/\s+/);
    return parseFloat(attrs[headerIndex["ACTIVE"]]);
  }

  function parseStatisticsShort(response) {
    if(!response){
      return undefined;
    }
    const [header, ...rows] = response.trim().split("\n");

    if(!rows || rows.length === 0){
      return undefined;
    }

    if (!headerIndex.current) {
      headerIndex.current = getIndexes(header);
    }

    const series = {};
    let time = null;
    let totalDownlink = 0;
    let totalUplink = 0;
    let activeSubscribers = {};
    let totalSubscribers = 0;
    let totalFlows = 0;
    let totalDeltaOptBytes = 0;
    let totalDeltaBytes = 0;

    for (let row of rows) {
      const attrs = row.trim().split(/\s+/);
      const bqn = Number(attrs[headerIndex.current["BQN"]]);
      const currentTime = Number(attrs[headerIndex.current["EPOCH"]]);
      const currentDownlink = Number(attrs[headerIndex.current["TX-BYTES-DN"]]);
      const currentUplink = Number(attrs[headerIndex.current["TX-BYTES-UP"]]);
      const tcpTotalBytes = Number(attrs[headerIndex.current["TX-TCP-BYTES"]]);
      const activeSubs = Number(attrs[headerIndex.current["ACTIVE-SUBS"]]);
      activeSubscribers[bqn] = activeSubs;
      totalSubscribers += activeSubs;

      const activeFlows = Number(attrs[headerIndex.current["ACTIVE-FLOWS"]]);
      totalFlows += activeFlows;

      if (!time) {
        time = new Date(currentTime);
      }

      const bqnKey = `BQN ${bqn}`;
      const deltaEpoc = lastValue.current[bqnKey]
        ? currentTime - lastValue.current[bqnKey].time
        : null;
      const lastDownlink = lastValue.current[bqnKey]
        ? lastValue.current[bqnKey].downlink
        : null;
      const lastUplink = lastValue.current[bqnKey]
        ? lastValue.current[bqnKey].uplink
        : null;
      const lastTcpBytes = lastValue.current[bqnKey]
        ? lastValue.current[bqnKey].tcpTotalBytes
        : null;

      const deltaBytesDownlink =
        lastDownlink === null ? 0 : Math.max(currentDownlink - lastDownlink, 0);
      const deltaBytesUplink =
        lastUplink === null ? 0 : Math.max(currentUplink - lastUplink, 0);

      const deltaTcpTotalBytes =
        lastTcpBytes === null ? 0 : Math.max(tcpTotalBytes - lastTcpBytes, 0);
      
      
      const downlink = deltaEpoc && deltaEpoc > 0 ? (deltaBytesDownlink / deltaEpoc / 125) : 0;
      const uplink = deltaEpoc && deltaEpoc > 0 ? (deltaBytesUplink / deltaEpoc / 125) : 0;

      totalDownlink += downlink;
      totalUplink += uplink;
      
      const tcpOptBytes = Number(attrs[headerIndex.current["TX-TCPO-BYTES"]]);
      const deltaOptBytes = lastValue.current[bqnKey]
        ? Math.max(tcpOptBytes - lastValue.current[bqnKey].tcpOptBytes, 0)
        : 0;
      totalDeltaOptBytes += deltaOptBytes;
      totalDeltaBytes += deltaTcpTotalBytes;

      series[bqnKey] = {
        downlink,
        uplink,
      };
      lastValue.current[bqnKey] = {
        downlink: currentDownlink,
        uplink: currentUplink,
        time: currentTime,
        tcpTotalBytes,
        tcpOptBytes,
      };

      maxValue.current = {
        downlink: Math.max(maxValue.current.downlink, downlink),
        uplink: Math.max(maxValue.current.uplink, uplink),
      };
    }

    const optPercentage =
      totalDeltaBytes === 0 ? 0 : (totalDeltaOptBytes / totalDeltaBytes) * 100;

    return {
      totalDownlink,
      totalUplink,
      throughput: { series, time },
      totalSubscribers,
      activeSubscribers,
      totalFlows,
      optPercentage,
    };
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Div>
        <Header title="Dashboard" contextMenu={<ContextMenu />} big />
        <div className="container-lg-2">
          <div className="row clearfix margin-t-15">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
              { document.visibilityState === "visible" && isMounted.current && (
                <ClusterSummary
                  totalDownlink={summary.totalDownlink}
                  totalUplink={summary.totalUplink}
                  totalSubscribers={summary.totalSubscribers}
                  totalFlows={summary.totalFlows}
                  optPercentage={summary.optPercentage}
                  totalSubscriberGroups={totalSubscriberGroups}
                />
              )}
            </div>
          </div>
          <ClusterThroughput
            maxValue={maxValue.current}
            resetSignal={resetSignal}
            item={{ ...data.throughput.series, time: data.throughput.time }}
            map={map}
            fields={fields}
            timeWindow={timeWindow}
            test={test}
          />
          <div className="row clearfix">
            {gridLimit > 0 && (
              <GridContainer
                className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-30"
                columns={nColumns}
              >
                {bqns.slice(0, gridLimit).map((bqn) => (
                  <BqnButton
                    key={bqn.bqnId}
                    bqn={{ ...bqn, hostname: hostnames[bqn.bqnId] }}
                    onClick={() => onBqnSelected(bqn)}
                    alarms={alarms}
                    big={bqns.length < 10}
                    login={bqn.bqnId.toString() === loginBqnId.toString()}
                    nSubscribers={data.activeSubscribers[bqn.bqnId] ?? 0}
                  />
                ))}
              </GridContainer>
            )}
            {gridLimit < bqns.length && (
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 flex-container margin-t-15 margin-b-30">
                {bqns.slice(gridLimit, bqns.length).map((bqn) => (
                  <BqnButton
                    key={bqn.bqnId}
                    bqn={{ ...bqn, hostname: hostnames[bqn.bqnId] }}
                    onClick={() => onBqnSelected(bqn)}
                    alarms={alarms}
                    big={bqns.length < 10}
                    login={bqn.bqnId.toString() === loginBqnId.toString()}
                    nSubscribers={data.activeSubscribers[bqn.bqnId] ?? 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Div>
      <HelpModal />
    </>
  );
}
